import { useCallback, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import Icon from '../Icon'
import Locations from './Locations'
import Button from '../Button'
import Transactions from '../../components/Transactions'
import { Divider, IconButton, TextField } from '@mui/material'
import {
  Close,
  AccessTime,
  Comment,
  People,
  DeleteForever,
  ArrowRightAltRounded,
  CheckCircleOutline,
  CheckCircle,
  Cancel,
  WatchLater,
  Forum,
  Restore,
  ReceiptLong,
  CalendarMonth,
  Person,
  AccessAlarm,
  GroupAdd,
  MoneyOff,
  SwapHoriz
} from '@mui/icons-material'
import { Avatar } from '@mui/material'
import Alert from '../Alert'
import UserInfo from '../UserInfo'
import SendComment from './SendComment'
import CancelLobby from './CancelLobby'
import UncancelLobby from './UncancelLobby'
import CompleteLobby from './CompleteLobby'
import Messages from '../../components/Messages'
import { faBoltLightning, faVanShuttle, faTaxi } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddSeats from './AddSeats'
import RefundActionDialog, { describeRefundResult, isFreeCompanion } from './RefundActionDialog'
import { Pill } from '../RefundPill'
import ChangeOwnerDialog from './ChangeOwnerDialog'
import formatPrice from '../../utils/formatPrice'
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { DIAS, MESES } from '../../config'
import usePermissions from '../../hooks/usePermissions'
import adminRequest from '../../utils/adminRequest'

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const STATUS_BANNER = {
  high: 'bg-coral text-white',
  medium: 'bg-amber text-espresso_dark',
  clear: 'bg-emerald text-white'
}

const STATUS_ICON = {
  high: Cancel,
  medium: WatchLater,
  clear: CheckCircle
}

const isLobbyPayload = data =>
  Boolean(data && typeof data === 'object' && data._id && Array.isArray(data.companions))

const LobbyInfo = ({ lobby: initialLobby, setSelected }) => {
  const [lobby, setLobby] = useState(initialLobby)
  const [departures, setDepartures] = useState([])
  const [adminComment, setAdminComment] = useState(initialLobby?.Admin?.comment)
  const [userAlert, setUserAlert] = useState(false)
  const [cancelAlert, setCancelAlert] = useState(false)
  const [cancelLobby, setCancelLobby] = useState(false)
  const [uncancelLobby, setUncancelLobby] = useState(false)
  const [completeAlert, setCompleteAlert] = useState(false)
  const [completeLobby, setCompleteLobby] = useState(false)
  const [visible, setVisible] = useState(Boolean(lobby))
  const [sendComment, setSendComment] = useState(false)
  const { hasPermission } = usePermissions()
  const canEditLobby = hasPermission('edit-lobby')
  const [openChat, setOpenChat] = useState(false)
  const [openTransactions, setOpenTransactions] = useState(false)
  const [userType, setUserType] = useState(false)
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false)
  const [entered, setEntered] = useState(prefersReducedMotion)
  const latestRefresh = useRef(0)

  useEffect(() => {
    if (prefersReducedMotion) return
    const id = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    latestRefresh.current += 1
    setLobby(initialLobby)
    setDepartures([])
  }, [initialLobby])

  const lobbyId = lobby?._id

  const refreshLobby = useCallback(async () => {
    latestRefresh.current += 1
    const refresh = latestRefresh.current
    const response = await adminRequest(`/admin/lobbies/${lobbyId}`)
    if (refresh !== latestRefresh.current) return
    if (response.ok && isLobbyPayload(response.data)) setLobby(response.data)
  }, [lobbyId])

  const handleCompanionDeparted = useCallback(
    (companion, outcome) => {
      setDepartures(previous => [
        ...previous.filter(entry => entry.companion.user_id !== companion.user_id),
        { companion, outcome }
      ])
      refreshLobby()
    },
    [refreshLobby]
  )

  const remainingIds = new Set((lobby.companions || []).map(companion => companion.user_id))
  const departedCompanions = departures.filter(entry => !remainingIds.has(entry.companion.user_id))

  let occupiedSeats = 0
  if (lobby.companions) {
    occupiedSeats = sumarArray(lobby.companions.map(x => x.seats))
  }

  const departureTime = lobby.Journey.departure_time.substr(
    lobby.Journey.departure_time.indexOf('T') + 1,
    5
  )
  const departureDateTimeCreated = new Date(new Date(lobby.created).getTime() - 3 * 60 * 60 * 1000)
  const departureTimeCreated = departureDateTimeCreated
    .toISOString()
    .substr(departureDateTimeCreated.toISOString().indexOf('T') + 1, 5)
  const departureDateCreated =
    DIAS[new Date(lobby.created).getDay()] +
    ' ' +
    new Date(lobby.created).getDate() +
    ' de ' +
    MESES[new Date(lobby.created).getMonth()]
  const departureDatetimeToShow = new Date(
    new Date(lobby.Journey.departure_time).getTime() + 3 * 60 * 60 * 1000
  ) //porqye si no en e lobby info muestra mal el dia en el caso de que sea la hora entra 00 - 03 AM
  const lastChangeDateTimeCreated = new Date(new Date(lobby.updated).getTime() - 3 * 60 * 60 * 1000)
  const lastChangeTimeCreated = lastChangeDateTimeCreated
    .toISOString()
    .substr(lastChangeDateTimeCreated.toISOString().indexOf('T') + 1, 5)
  const lastChangeDateCreated =
    DIAS[new Date(lobby.updated).getDay()] +
    ' ' +
    new Date(lobby.updated).getDate() +
    ' de ' +
    MESES[new Date(lobby.updated).getMonth()]
  let cancelledDateTime
  let cancelledTime
  let cancelledDate
  if (lobby.cancelled && lobby.cancelled_time) {
    cancelledDateTime = new Date(new Date(lobby.cancelled_time).getTime() - 3 * 60 * 60 * 1000)
    cancelledTime = cancelledDateTime
      .toISOString()
      .substr(cancelledDateTime.toISOString().indexOf('T') + 1, 5)
    cancelledDate =
      DIAS[new Date(lobby.cancelled_time).getDay()] +
      ' ' +
      new Date(lobby.cancelled_time).getDate() +
      ' de ' +
      MESES[new Date(lobby.cancelled_time).getMonth()]
  }

  let arrivalTime = lobby.Journey.estimated_time.substr(
    lobby.Journey.estimated_time.indexOf('T') + 1,
    5
  )
  const departureDate =
    DIAS[departureDatetimeToShow.getDay()] +
    ' ' +
    departureDatetimeToShow.getDate() +
    ' de ' +
    MESES[departureDatetimeToShow.getMonth()]
  const today = new Date()
  const late =
    new Date(lobby.Journey.estimated_time).getTime() + 3 * 60 * 60 * 1000 - today.getTime() < 0
  const cancelled = lobby.cancelled

  const statusTone = cancelled ? 'high' : lobby.completed ? 'clear' : late ? 'medium' : null
  const statusLabel = cancelled ? 'Cancelado' : lobby.completed ? 'Concretado' : late ? 'Terminado' : null

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      paddingTop: 20,
      paddingBottom: 0,
      paddingLeft: 20,
      paddingRight: 20,
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'start',
      overflow: 'hidden',
      borderRadius: 0,
      borderWidth: 0
    },
    overlay: {
      zIndex: 100,
      backgroundColor: 'rgba(0,0,0,0)'
    }
  }

  const handleClose = () => {
    setVisible(false)
    setSelected({})
  }

  const handleDelete = () => {
    setCancelAlert(true)
  }

  const handleComplete = () => {
    setCompleteAlert(true)
  }

  const handleOpenChat = () => {
    setOpenChat(true)
  }

  const handleOpenTransactions = () => {
    setOpenTransactions(true)
  }

  const handleUncancel = () => {
    setUncancelLobby(true)
  }

  const handleCopyId = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(String(lobby._id))
  }

  // Recarga con el deep-link ?lobby_id= para que la lista y el detalle muestren al nuevo conductor.
  const handleOwnerChanged = () => {
    const params = new URLSearchParams(window.location.search)
    params.set('lobby_id', String(lobby._id))
    window.location.assign(`${window.location.pathname}?${params.toString()}`)
  }

  return (
    <>
      <Modal
        isOpen={visible}
        onRequestClose={handleClose}
        style={customStyles}
        contentLabel='Example Modal'
      >
        <div className='w-full justify-end flex gap-2'>
          {hasPermission('channels') && <IconButton onClick={handleOpenChat} style={{ paddingTop: '0' }}>
            <Forum />
          </IconButton>}
          <IconButton onClick={handleOpenTransactions} style={{ paddingTop: '0' }}>
            <ReceiptLong />
          </IconButton>
          {canEditLobby && !lobby.completed && late && (
            <IconButton onClick={handleComplete} style={{ paddingTop: '0' }}>
              <CheckCircleOutline />
            </IconButton>
          )}
          {canEditLobby && !lobby.cancelled && (
            <IconButton onClick={handleDelete} style={{ paddingTop: '0' }}>
              <DeleteForever />
            </IconButton>
          )}
          {canEditLobby && lobby.cancelled && (
            <IconButton onClick={handleUncancel} style={{ paddingTop: '0' }}>
              <Restore />
            </IconButton>
          )}
          <IconButton onClick={handleClose} style={{ paddingTop: '0' }}>
            <Close />
          </IconButton>
        </div>
        <Divider />
        <div
          className={`flex flex-col items-center grow overflow-auto pb-6 transition-all duration-500 ease-out ${
            entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
        >
          {statusTone && (
            <div
              className={`sticky top-0 z-10 -mx-5 w-[calc(100%+40px)] flex flex-col items-center gap-0.5 px-5 py-3 ${STATUS_BANNER[statusTone]}`}
            >
              <div className='flex items-center gap-2 text-sm font-extrabold tracking-wide uppercase'>
                {(() => {
                  const StatusIcon = STATUS_ICON[statusTone]
                  return <StatusIcon fontSize='small' />
                })()}
                {statusLabel}
              </div>
              {cancelled && lobby.cancelled_time && (
                <div className='text-xs font-medium opacity-90'>
                  {cancelledDate} · {cancelledTime}
                  {lobby.cancelled_reason && ` — ${lobby.cancelled_reason}`}
                </div>
              )}
            </div>
          )}

          <div className='w-full flex flex-col items-center'>
            <div
              className='cursor-pointer text-center flex flex-col items-center justify-center mt-4'
              onClick={() => {
                setUserAlert(lobby.Owner_info.id)
                setUserType('owner')
              }}
            >
              <img
                className='rounded-full w-28 h-28 cursor-pointer object-cover ring-4 ring-warm_border dark:ring-dk-border shadow-sm'
                src={lobby.Owner_info.picture}
              />
              <p className='mt-3 text-xl font-bold tracking-tight text-espresso dark:text-white cursor-pointer'>
                {lobby.Owner_info.first_name}
              </p>
            </div>

            <button
              type='button'
              onClick={handleCopyId}
              title='Copiar ID de la lobby'
              className='mt-2 font-mono text-[11px] text-warm_gray dark:text-gray-400 bg-ink/[0.04] dark:bg-dk-raised hover:bg-ink/[0.08] dark:hover:bg-dk-hover rounded-md px-2 py-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30'
            >
              ID {lobby._id}
            </button>

            {canEditLobby && !lobby.cancelled && !lobby.completed && (
              <button
                type='button'
                onClick={() => setChangeOwnerOpen(true)}
                className='mt-2 flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-espresso dark:text-gray-200 bg-ink/[0.05] hover:bg-ink/[0.1] dark:bg-dk-hover dark:hover:bg-dk-border transition-colors'
              >
                <SwapHoriz fontSize='small' />
                Cambiar conductor
              </button>
            )}

            <div className='flex flex-wrap justify-center gap-2 mt-4'>
              {lobby.auto_booking ? (
                <span className='inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold tracking-wide bg-coral/[0.12] text-coral_deep ring-1 ring-coral/30 dark:bg-red-500/10 dark:text-red-400'>
                  <FontAwesomeIcon icon={faBoltLightning} />
                  Reserva automática
                </span>
              ) : (
                <span className='inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold tracking-wide bg-ink/[0.06] text-espresso dark:bg-dk-raised dark:text-gray-200'>
                  <img
                    className='w-3.5 h-3.5'
                    src={require('../../assets/icons/join-requested-primary.png')}
                    alt='auto_booking'
                  />
                  Solicitud de reserva
                </span>
              )}
              {(lobby.type === 'van' || lobby.type === 'taxi') && (
                <span className='inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold tracking-wide bg-amber/[0.18] text-amber_deep ring-1 ring-amber/40 dark:bg-amber-500/10 dark:text-amber-400'>
                  <FontAwesomeIcon icon={lobby.type === 'van' ? faVanShuttle : faTaxi} />
                  Chofer
                </span>
              )}
            </div>
          </div>

          <div className='w-full flex flex-col gap-6 mt-6'>
            {lobby.comment && (
              <div className='flex gap-3 rounded-xl bg-coral/[0.06] dark:bg-dk-raised border-l-4 border-coral pl-4 pr-4 py-3'>
                <Comment style={{ color: '#c65944' }} fontSize='small' className='shrink-0 mt-0.5' />
                <p className='text-sm text-espresso dark:text-gray-200 italic'>{lobby.comment}</p>
              </div>
            )}

            <Locations
              departureDate={departureDate}
              departureTime={departureTime}
              arrivalTime={arrivalTime}
              originName={lobby.Journey.origin_name.formatted_address}
              specificOriginName={lobby.Journey.origin_name.specific_address}
              destinationName={lobby.Journey.destination_name.formatted_address}
              specificDestinationName={lobby.Journey.destination_name.specific_address}
            />

            <div className='pt-5 border-t border-warm_border dark:border-dk-border grid grid-cols-2 gap-x-4 gap-y-4 text-left'>
              <div>
                <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                  Precio
                </div>
                <div className='text-base font-semibold text-espresso dark:text-gray-200 mt-0.5'>
                  ${formatPrice(lobby.price)} (${formatPrice(lobby.price_without_fee)} sin comisión)
                </div>
              </div>
              <div>
                <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                  Asientos disponibles
                </div>
                <div className='text-base font-semibold text-espresso dark:text-gray-200 mt-0.5'>
                  {lobby.seats_available - occupiedSeats}
                </div>
              </div>
              {lobby.Journey.stops?.length > 0 && (
                <div className='col-span-2'>
                  <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                    Paradas
                  </div>
                  <div className='text-base font-semibold text-espresso dark:text-gray-200 mt-0.5'>
                    {lobby.Journey.stops.map(x => x.location_name).toString()}
                  </div>
                </div>
              )}
              <div>
                <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                  Creado
                </div>
                <div className='text-base font-semibold text-espresso dark:text-gray-200 mt-0.5'>
                  {departureDateCreated} · {departureTimeCreated}
                </div>
              </div>
              {lobby.payment && (
                <div>
                  <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                    Medio de pago
                  </div>
                  <div className='text-base font-semibold text-espresso dark:text-gray-200 mt-0.5'>{lobby.payment}</div>
                </div>
              )}
            </div>

            {lobby.changes.length !== 0 && (
              <div className='flex flex-col gap-3'>
                <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                  Último cambio: {lastChangeDateCreated} · {lastChangeTimeCreated}
                </div>
                <div className='flex flex-col gap-3 rounded-2xl bg-cream dark:bg-dk-raised ring-1 ring-warm_border dark:ring-dk-border p-4'>
                  <div className='text-[11px] uppercase tracking-wider text-warm_gray dark:text-gray-400 font-medium'>
                    Datos anteriores cambiados por el conductor
                  </div>
                  {lobby.changes.map((change, index) => (
                    <Change key={change.updated ?? index} change={change} occupiedSeats={occupiedSeats} />
                  ))}
                </div>
              </div>
            )}

            {lobby.companions.length >= 1 && (
              <div className='flex flex-col gap-3 pt-5 border-t border-warm_border dark:border-dk-border'>
                <div className='text-xs uppercase tracking-wider font-bold text-warm_gray dark:text-gray-400'>
                  Acompañantes ({lobby.companions.length})
                </div>
                <div className='flex flex-wrap gap-4'>
                  {lobby.companions.map(companion => (
                    <Companion
                      key={companion.user_id}
                      companion={companion}
                      setUserAlert={setUserAlert}
                      setUserType={setUserType}
                      lobby={lobby}
                      onDeparted={handleCompanionDeparted}
                      onOpenTransactions={handleOpenTransactions}
                    />
                  ))}
                </div>
              </div>
            )}

            {departedCompanions.length > 0 && (
              <div
                className='flex flex-col gap-3 pt-5 border-t border-warm_border dark:border-dk-border'
                data-testid='departed-companions'
              >
                <div className='text-xs uppercase tracking-wider font-bold text-warm_gray dark:text-gray-400'>
                  Salieron del viaje ({departedCompanions.length})
                </div>
                <div className='flex flex-wrap gap-4'>
                  {departedCompanions.map(({ companion, outcome }) => (
                    <Companion
                      key={companion.user_id}
                      companion={companion}
                      setUserAlert={setUserAlert}
                      setUserType={setUserType}
                      lobby={lobby}
                      initialOutcome={outcome}
                    />
                  ))}
                </div>
              </div>
            )}

            {lobby.pending_companions?.length > 0 && (
              <div className='bg-coral rounded-2xl shadow-sm p-5'>
                <div className='text-xs uppercase tracking-wider font-bold text-white/80 mb-3'>
                  Acompañantes pendientes
                </div>
                <div className='flex flex-wrap gap-4'>
                  {lobby.pending_companions.map(companion => (
                    <Companion
                      key={companion.user_id}
                      companion={companion}
                      lobby={lobby}
                      setUserAlert={setUserAlert}
                      setUserType={setUserType}
                      pendingCompanion={true}
                    />
                  ))}
                </div>
              </div>
            )}

          </div>

          {canEditLobby && (
            <div className='w-full flex flex-col mt-6 pt-5 border-t border-warm_border dark:border-dk-border gap-3'>
              <TextField
                multiline
                label='Comentario de administrador'
                value={adminComment}
                onChange={evt => setAdminComment(evt.target.value)}
              />
              <Button
                name={'Enviar comentario'}
                style={{
                  zIndex: 300,
                  fontSize: '0.8rem',
                  paddingBottom: '10px',
                  paddingTop: '10px'
                }}
                onClick={() => setSendComment(true)}
              />
            </div>
          )}
          <Alert
            hideButton
            visible={userAlert}
            onClose={() => setUserAlert(false)}
            style={{ height: 510 }}
          >
            <UserInfo userId={userAlert} lobby={lobby} type={userType} />
          </Alert>
          <Alert
            hideButton
            visible={cancelAlert}
            onSubmit={() => {
              setCancelLobby(true)
              setCancelAlert(false)
            }}
            onClose={() => setCancelAlert(false)}
          >
            <div className='flex flex-col justify-center'>
              <div className='text-base'>¿Estás seguro de que quieres cancelar esta lobby?</div>
              <div className='text-base text-green-400'>
                Se reembolsa a todos los pasajeros. Si alguno tiene la opción de saldo, elige cómo recibirlo
                en las próximas 24 h; si no elige, va a su medio de pago original. Lo que se pagó con saldo
                vuelve al saldo.
              </div>
            </div>
          </Alert>
          <Alert
            hideButton
            visible={completeAlert}
            onSubmit={() => {
              setCompleteLobby(true)
              setCompleteAlert(false)
            }}
            onClose={() => setCompleteAlert(false)}
          >
            <div className='flex flex-col justify-center'>
              <div className='text-base'>
                ¿Estás seguro de que quieres marcar como completada esta lobby?
              </div>
            </div>
          </Alert>
          <Alert
            hideButton
            visible={openTransactions}
            onClose={() => setOpenTransactions(false)}
            style={{ height: 510 }}
            fullScreen
          >
            <Transactions lobbyId={String(lobby._id)} />
          </Alert>
          {sendComment && (
            <SendComment lobby={lobby} comment={adminComment} setSendComment={setSendComment} />
          )}
          {cancelLobby && <CancelLobby lobby={lobby} />}
          {uncancelLobby && <UncancelLobby lobby={lobby} />}
          {completeLobby && <CompleteLobby lobby={lobby} />}
          {canEditLobby && (
            <ChangeOwnerDialog
              open={changeOwnerOpen}
              lobby={lobby}
              onClose={() => setChangeOwnerOpen(false)}
              onChanged={handleOwnerChanged}
            />
          )}
        </div>
      </Modal>
      {openChat && (
        <Messages
          visible={openChat}
          setVisible={setOpenChat}
          initialLobbyId={lobby._first_id ?? lobby._old_id ?? lobby._id}
        />
      )}
    </>
  )
}

const Change = ({ change, occupiedSeats }) => {
  const departureDatetimeToShow = new Date(
    new Date(change.departure_time).getTime() + 3 * 60 * 60 * 1000
  )
  const departureDateTime =
    DIAS[departureDatetimeToShow.getDay()] +
    ' ' +
    departureDatetimeToShow.getDate() +
    '/' +
    departureDatetimeToShow.getMonth() +
    ' ' +
    departureDatetimeToShow.getHours() +
    ':' +
    departureDatetimeToShow.getMinutes()
  const changeDatetimeToShow = new Date(new Date(change.updated).getTime())
  const changeDate =
    String(changeDatetimeToShow.getDate()).padStart(2, '0') +
    '/' +
    String(changeDatetimeToShow.getMonth()).padStart(2, '0')
  const changeTime =
    changeDatetimeToShow.getHours().toString().padStart(2, '0') +
    ':' +
    changeDatetimeToShow.getMinutes().toString().padStart(2, '0')

  return (
    <div className='flex flex-col rounded-xl bg-white dark:bg-dk-surface border border-warm_border dark:border-dk-border p-4 gap-2'>
      <div className='flex gap-3 text-sm'>
        <div className='flex gap-1.5'>
          <People style={{ color: '#c65944' }} fontSize='small' />
          <div className='flex flex-col justify-center'>
            {change.seats_available - occupiedSeats + ' asientos disponibles'}
          </div>
        </div>
        <div className='flex gap-1.5'>
          <AccessTime style={{ color: '#c65944' }} fontSize='small' />
          <div className='flex flex-col justify-center'>Salida: {departureDateTime}</div>
        </div>
      </div>
      <div className='flex gap-3 text-sm'>
        <div className='w-full flex gap-2 items-center font-bold text-coral_deep dark:text-coral'>
          <Icon img='peso.png' height={12} width={28} />
          <div className='flex flex-row'>{formatPrice(change.price)}</div>
        </div>
        <div className='w-full flex gap-2 text-xs text-warm_gray dark:text-gray-400'>
          <div className='w-full flex justify-end'>
            {changeDate} a las {changeTime}
          </div>
        </div>
      </div>
    </div>
  )
}

const CompanionTrip = ({ companion, stops, lobby }) => {
  const [origin, setOrigin] = useState(lobby.Journey.origin_name.locality)
  const [destination, setDestination] = useState(lobby.Journey.destination_name.locality)

  useEffect(() => {
    stops?.forEach(stop => {
      if (
        JSON.stringify(companion.start.coordinates) == JSON.stringify(stop.location.coordinates)
      ) {
        setOrigin(stop.location_name)
      }
      if (JSON.stringify(companion.end.coordinates) == JSON.stringify(stop.location.coordinates)) {
        setDestination(stop.location_name)
      }
    })
  }, [])

  return (
    <div className='flex gap-2 items-center text-base'>
      {origin} <ArrowRightAltRounded fontSize='large' /> {destination}
    </div>
  )
}

const sumarArray = arr => {
  let sum = 0
  for (let i = 0; i < arr.length; i += 1) {
    sum += arr[i]
  }
  return sum
}

const Companion = ({
  companion,
  setUserAlert,
  setUserType,
  lobby,
  pendingCompanion = false,
  initialOutcome = null,
  onDeparted,
  onOpenTransactions
}) => {
  const { hasPermission } = usePermissions()
  const canEditLobby = hasPermission('edit-lobby')
  const [companionSeats, setCompanionSeats] = useState(companion.seats)
  const [addSeatsModal, setAddSeatsModal] = useState(false)
  const [seatsToAdd, setSeatsToAdd] = useState(1)
  const [addSeatsData, setAddSeatsData] = useState(null)
  const [refundAction, setRefundAction] = useState(null)
  const [outcome, setOutcome] = useState(initialOutcome)
  const freeCompanion = isFreeCompanion(companion)

  const closeRefundDialog = () => {
    setRefundAction(null)
    if (outcome && onDeparted) onDeparted(companion, outcome)
  }

  const handleAddSeats = () => {
    setSeatsToAdd(1)
    setAddSeatsModal(true)
  }

  const handleConfirmAddSeats = () => {
    setAddSeatsData({
      lobby,
      companion,
      seatsToAdd
    })
    setAddSeatsModal(false)
  }

  const handleCloseAddSeatsModal = () => {
    setAddSeatsModal(false)
  }

  const handleSuccess = () => {
    setCompanionSeats(prev => prev + seatsToAdd)
    setAddSeatsData(null)
  }

  return (
    <>
      <div className='bg-white dark:bg-dk-surface border border-warm_border dark:border-dk-border rounded-2xl overflow-hidden w-full sm:w-[600px] shadow-sm'>
        <div className='bg-cream/60 dark:bg-dk-raised px-4 py-3 flex items-center justify-between border-b border-warm_border dark:border-dk-border'>
          <div className='flex items-center gap-3'>
            <Avatar
              src={companion.picture}
              className='ring-2 ring-warm_border dark:ring-dk-border'
              sx={{ width: 32, height: 32 }}
            >
              {companion.first_name?.charAt(0)}
            </Avatar>
            <span
              className='text-sm sm:text-base cursor-pointer text-light_coral font-semibold'
              onClick={() => {
                setUserAlert(companion.user_id)
                setUserType('lobby-companion')
              }}
            >
              {companion.first_name} {companion.last_name}
            </span>
          </div>
          {canEditLobby && !lobby.cancelled && !outcome && (
            <div className='flex flex-wrap gap-2 items-center justify-end'>
              {lobby.seats_remaining > 0 && (
                <button
                  type='button'
                  onClick={handleAddSeats}
                  className='flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-espresso dark:text-gray-200 bg-ink/[0.05] hover:bg-ink/[0.1] dark:bg-dk-hover dark:hover:bg-dk-border transition-colors'
                >
                  <GroupAdd fontSize='small' />
                  Asientos
                </button>
              )}
              {!pendingCompanion && !freeCompanion && (
                <button
                  type='button'
                  onClick={() => setRefundAction('partial')}
                  className='flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-amber_deep hover:bg-amber/[0.15] dark:text-amber-400 dark:hover:bg-amber-500/10 transition-colors'
                >
                  <MoneyOff fontSize='small' />
                  Reembolso 50%
                </button>
              )}
              {!pendingCompanion && (
                <button
                  type='button'
                  onClick={() => setRefundAction('delete')}
                  className='flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-coral_deep dark:text-red-400 hover:bg-coral/[0.1] dark:hover:bg-red-500/10 transition-colors'
                >
                  <DeleteForever fontSize='small' />
                  Eliminar
                </button>
              )}
            </div>
          )}
        </div>

        {outcome && <CompanionOutcome outcome={outcome} />}

        <div className='p-4'>
          <div className='flex justify-between mb-3 sm:flex-row flex-col gap-4'>
            <div className='flex items-start gap-3'>
              <Person fontSize='small' className='mt-0.5' />
              <div className='flex flex-col items-start'>
                <button
                  type='button'
                  onClick={() => {
                    if (navigator.clipboard) navigator.clipboard.writeText(String(companion.user_id))
                  }}
                  title='Copiar ID'
                  className='w-fit text-left text-xs font-mono text-warm_gray dark:text-gray-400 hover:text-espresso dark:hover:text-gray-200 transition-colors'
                >
                  {companion.user_id}
                </button>
                {companion.phone_number && (
                  <div className='text-sm text-warm_gray dark:text-gray-400 mt-1'>{companion.phone_number}</div>
                )}
              </div>
            </div>

            <div className='w-full sm:w-auto flex flex-col gap-1 text-sm'>
              <div className='flex items-center justify-between gap-4'>
                <span className='text-warm_gray dark:text-gray-400'>
                  {companionSeats}x asiento{companionSeats > 1 ? 's' : ''}
                </span>
                <span className='tabular-nums text-espresso dark:text-gray-200'>
                  ${formatPrice(companion.price)}
                </span>
              </div>
              {companion.fee > 0 && (
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-warm_gray dark:text-gray-400'>Comisión</span>
                  <span className='tabular-nums text-espresso dark:text-gray-200'>
                    ${formatPrice(companion.fee)}
                  </span>
                </div>
              )}
              {companion.discount > 0 && (
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-warm_gray dark:text-gray-400'>
                    Descuento{companion.coupon_code ? ` (${companion.coupon_code})` : ''}
                  </span>
                  <span className='tabular-nums text-emerald_deep dark:text-emerald'>
                    -${formatPrice(companion.discount)}
                  </span>
                </div>
              )}
              <div className='flex items-center justify-between gap-4 pt-1 mt-0.5 border-t border-warm_border dark:border-dk-border'>
                <span className='font-bold text-espresso dark:text-white'>Total</span>
                <span className='font-bold tabular-nums text-coral_deep dark:text-coral'>
                  ${formatPrice(companion.price + companion.fee - (companion.discount ?? 0))}
                </span>
              </div>
            </div>
          </div>

          <div className='mb-3'>
            <CompanionTrip companion={companion} stops={lobby.Journey.stops} lobby={lobby} />
          </div>

          <div className='flex items-center justify-between text-xs text-warm_gray dark:text-gray-400'>
            <div className='flex items-center gap-2'>
              <CalendarMonth fontSize='inherit' />
              {companion.created && new Date(companion.created).toLocaleString()}
            </div>
          </div>

          {companion.expiration && (
            <div className='flex items-center justify-between text-xs text-warm_gray dark:text-gray-400 mt-2'>
              <div className='flex items-center gap-2'>
                <AccessAlarm fontSize='inherit' />
                {new Date(companion.expiration).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>

      <AddSeatsModal
        lobby={lobby}
        companion={companion}
        seatsToAdd={seatsToAdd}
        addSeatsModal={addSeatsModal}
        setSeatsToAdd={setSeatsToAdd}
        handleConfirmAddSeats={handleConfirmAddSeats}
        handleCloseAddSeatsModal={handleCloseAddSeatsModal}
      />

      {addSeatsData && (
        <AddSeats
          lobby={addSeatsData.lobby}
          companion={addSeatsData.companion}
          seatsToAdd={addSeatsData.seatsToAdd}
          onClose={() => setAddSeatsData(null)}
          handleSuccess={handleSuccess}
        />
      )}

      {refundAction && (
        <RefundActionDialog
          action={refundAction}
          lobby={lobby}
          companion={companion}
          onClose={closeRefundDialog}
          onDone={setOutcome}
          onOpenTransactions={onOpenTransactions}
        />
      )}
    </>
  )
}

const CompanionOutcome = ({ outcome }) => {
  const { tone, text } = describeRefundResult(outcome)
  return (
    <div
      role='status'
      className='flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5 border-b border-warm_border dark:border-dk-border bg-cream/70 dark:bg-dk-raised/60'
    >
      <Pill tone={tone}>
        {outcome.action === 'partial' ? 'Reembolso parcial enviado' : 'Eliminado del viaje'}
      </Pill>
      <span className='text-xs text-espresso dark:text-gray-300'>{text}</span>
    </div>
  )
}

const AddSeatsModal = ({
  lobby,
  companion,
  seatsToAdd,
  addSeatsModal,
  setSeatsToAdd,
  handleConfirmAddSeats,
  handleCloseAddSeatsModal
}) => {
  const discount = companion.discount ?? 0

  return (
    <Alert
      hideButton={false}
      visible={addSeatsModal}
      onSubmit={handleConfirmAddSeats}
      onClose={handleCloseAddSeatsModal}
    >
      <div className='flex flex-col justify-center gap-4'>
        <div className='text-lg'>
          Agregar asientos a {companion.first_name} {companion.last_name}
        </div>
        <FormControl className='w-[100px] self-center'>
          <InputLabel id='seats-select-label'>Asientos</InputLabel>
          <Select
            labelId='seats-select-label'
            value={seatsToAdd}
            label='Asientos'
            onChange={e => setSeatsToAdd(e.target.value)}
          >
            {Array.from({ length: lobby.seats_remaining }, (_, i) => i + 1).map(num => (
              <MenuItem key={num} value={num}>
                {num}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <p className='text-center text-lg font-bold my-2'>
          Total: $
          {formatPrice(
            (seatsToAdd * (companion.price + companion.fee - discount)) / companion.seats
          )}
        </p>
      </div>
    </Alert>
  )
}

export default LobbyInfo
